import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import SuccessModal from './SuccessModal';

const ContributorForm = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    website: '',
    linkedin: '',
    twitter: '',
    expertise: [],
    experience: '',
    previousWork: '',
    bio: '',
    agreedToTerms: false,
    website_url: '' // Honeypot field
  });

  const expertiseOptions = [
    'Gold',
    'Silver',
    'Platinum',
    'Palladium',
    'Precious Metals Mining',
    'Cryptocurrency',
    'Bitcoin',
    'Ethereum',
    'Market Analysis',
    'Technical Analysis'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleExpertiseToggle = (item) => {
    setFormData(prev => ({
      ...prev,
      expertise: prev.expertise.includes(item)
        ? prev.expertise.filter(e => e !== item)
        : [...prev.expertise, item]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all required fields
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      toast.error("Please enter your first and last name.");
      return;
    }

    if (!formData.email) {
      toast.error("Please enter your email address.");
      return;
    }

    if (formData.expertise.length === 0) {
      toast.error("Please select at least one area of expertise.");
      return;
    }

    if (!formData.experience) {
      toast.error("Please select your years of experience.");
      return;
    }

    if (!formData.previousWork || formData.previousWork.length < 10) {
      toast.error("Please provide details about your previous work (at least 10 characters).");
      return;
    }

    if (!formData.bio || formData.bio.length < 50) {
      toast.error("Please provide a professional bio (at least 50 characters).");
      return;
    }

    if (!formData.agreedToTerms) {
      toast.error("Please agree to the terms and conditions.");
      return;
    }

    try {
      // Submit to backend
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contributors/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      // Check if response is ok before trying to parse JSON
      if (!response.ok) {
        let errorMessage = 'Failed to submit application';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          // If JSON parsing fails, use default message
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Show success modal
      setSubmittedEmail(formData.email);
      setShowSuccessModal(true);
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        website: '',
        linkedin: '',
        twitter: '',
        expertise: [],
        experience: '',
        previousWork: '',
        bio: '',
        agreedToTerms: false,
        website_url: ''
      });
    } catch (error) {
      console.error('Submission error:', error);
      toast.error("Submission Failed", {
        description: error.message || "An error occurred while submitting your application. Please try again.",
      });
    }
  };

  return (
    <>
      <SuccessModal 
        isOpen={showSuccessModal} 
        onClose={() => setShowSuccessModal(false)}
        applicantEmail={submittedEmail}
      />
      <form onSubmit={handleSubmit} className="contributor-form">
      {/* Honeypot field - hidden from users, catches bots */}
      <input
        type="text"
        name="website_url"
        value={formData.website_url}
        onChange={handleInputChange}
        style={{ display: 'none' }}
        tabIndex="-1"
        autoComplete="off"
      />
      
      <div className="form-grid">
        <div className="form-group">
          <Label htmlFor="firstName">First Name *</Label>
          <Input
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            required
            placeholder="John"
          />
        </div>
        
        <div className="form-group">
          <Label htmlFor="lastName">Last Name *</Label>
          <Input
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            required
            placeholder="Doe"
          />
        </div>
      </div>

      <div className="form-group">
        <Label htmlFor="email">Email Address *</Label>
        <Input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          required
          placeholder="john.doe@example.com"
        />
      </div>

      <div className="form-group">
        <Label htmlFor="website">Website / Blog URL</Label>
        <Input
          id="website"
          name="website"
          type="text"
          value={formData.website}
          onChange={handleInputChange}
          placeholder="yourwebsite.com or https://yourwebsite.com"
        />
        <span className="field-hint">You can enter with or without https://</span>
      </div>

      <div className="form-grid">
        <div className="form-group">
          <Label htmlFor="linkedin">LinkedIn Profile</Label>
          <Input
            id="linkedin"
            name="linkedin"
            type="text"
            value={formData.linkedin}
            onChange={handleInputChange}
            placeholder="linkedin.com/in/yourprofile"
          />
          <span className="field-hint">URL or username</span>
        </div>
        
        <div className="form-group">
          <Label htmlFor="twitter">Twitter / X Handle</Label>
          <Input
            id="twitter"
            name="twitter"
            type="text"
            value={formData.twitter}
            onChange={handleInputChange}
            placeholder="yourhandle or @yourhandle"
          />
          <span className="field-hint">With or without @</span>
        </div>
      </div>

      <div className="form-group">
        <Label>Areas of Expertise *</Label>
        <div className="expertise-grid">
          {expertiseOptions.map((item) => (
            <div key={item} className="expertise-item">
              <Checkbox
                id={item}
                checked={formData.expertise.includes(item)}
                onCheckedChange={() => handleExpertiseToggle(item)}
              />
              <label htmlFor={item} className="expertise-label">{item}</label>
            </div>
          ))}
        </div>
      </div>

      <div className="form-group">
        <Label htmlFor="experience">Professional Experience *</Label>
        <Select name="experience" value={formData.experience} onValueChange={(value) => setFormData(prev => ({ ...prev, experience: value }))} required>
          <SelectTrigger>
            <SelectValue placeholder="Select your experience level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0-2">0-2 years</SelectItem>
            <SelectItem value="3-5">3-5 years</SelectItem>
            <SelectItem value="6-10">6-10 years</SelectItem>
            <SelectItem value="10+">10+ years</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="form-group">
        <Label htmlFor="previousWork">Previous Publications / Portfolio Links *</Label>
        <Textarea
          id="previousWork"
          name="previousWork"
          value={formData.previousWork}
          onChange={handleInputChange}
          required
          placeholder="Please provide links to 2-3 examples of your published work or analysis. Include URLs separated by commas or line breaks."
          rows={5}
        />
      </div>

      <div className="form-group">
        <Label htmlFor="bio">Professional Bio *</Label>
        <Textarea
          id="bio"
          name="bio"
          value={formData.bio}
          onChange={handleInputChange}
          required
          placeholder="Tell us about your background, credentials, and why you'd be a great fit as a Kitco News contributor (150-300 words)."
          rows={5}
        />
      </div>

      <div className="form-group terms-group">
        <div className="terms-checkbox">
          <Checkbox
            id="terms"
            checked={formData.agreedToTerms}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, agreedToTerms: checked }))}
          />
          <label htmlFor="terms" className="terms-label">
            I agree to the terms and conditions and understand that Kitco News does not provide monetary compensation for contributors. *
          </label>
        </div>
      </div>

      <Button type="submit" className="submit-button" size="lg">
        Submit Application
      </Button>
    </form>
    </>
  );
};

export default ContributorForm;
